// src/pages/VerifyEmail.js
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import ContactAdminBox from "../admin/ContactAdminBox";
import "./Login.css";

export default function VerifyEmail() {
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isRateLimited, setIsRateLimited] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserEmail(user.email || "");

        // Get name from localStorage (saved during registration)
        const pendingName = localStorage.getItem("pendingUserName");
        if (pendingName) {
          setUserName(pendingName);
        }

        try {
          // Check if user doc exists (exists = verified)
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            // User is verified, redirect to dashboard
            navigate("/dashboard", { replace: true });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        navigate("/login", { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (location.state?.email) {
      setUserEmail(location.state.email);
    }
    if (location.state?.name) {
      setUserName(location.state.name);
    }
  }, [location.state]);

  async function handleCheckVerification() {
    setChecking(true);
    setMessage("");

    try {
      const user = auth.currentUser;
      if (user) {
        // Check if user doc exists in Firestore (exists = verified)
        const userDoc = await getDoc(doc(db, "users", user.uid));
        
        if (userDoc.exists()) {
          setMessage("✅ Email verified! Redirecting to dashboard...");
          setTimeout(() => {
            navigate("/dashboard", { replace: true });
          }, 1500);
        } else {
          setMessage("⚠️ Email not verified yet. Please check your inbox and click the verification link.");
        }
      }
    } catch (error) {
      console.error("Error checking verification:", error);
      setMessage("❌ Error checking verification. Please try again.");
    } finally {
      setChecking(false);
    }
  }

  async function handleResendEmail() {
    if (isRateLimited) {
      setMessage("⏳ You've reached the rate limit. Please wait 1 hour before requesting another email.");
      return;
    }

    setResending(true);
    setMessage("");

    try {
      const user = auth.currentUser;
      if (!user) {
        setMessage("❌ You must be logged in to resend verification email.");
        return;
      }

      // Get fresh token
      const idToken = await user.getIdToken(true);

      const functionUrl = "https://us-central1-craic-on-the-case.cloudfunctions.net/resendVerificationEmail";

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
      });

      const result = await response.json();

      // Handle rate limiting (429 status)
      if (response.status === 429) {
        setIsRateLimited(true);
        setMessage("⏳ You've sent too many verification emails. Please wait 1 hour and try again. Check your spam folder for previous emails.");
        return;
      }

      if (response.ok && result.success) {
        setMessage("📧 Verification email has been resent! Please check your inbox (including spam folder).");
      } else {
        setMessage(`❌ ${result.message || "Error sending email. Please try again."}`);
      }
    } catch (error) {
      console.error("Error resending email:", error);
      setMessage("❌ Error sending email. Please try again.");
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-overlay"></div>

      <div className="login-container" style={{
        background: 'rgba(20, 20, 20, 0.95)',
        padding: '40px',
        maxWidth: '520px'
      }}>
        <div style={{
          fontSize: '72px',
          textAlign: 'center',
          marginBottom: '20px',
          filter: 'drop-shadow(0 0 10px rgba(255, 51, 102, 0.3))'
        }}>
          📧
        </div>

        <h2 style={{
          color: '#ff3366',
          fontSize: '2rem',
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          Verify Your Email
        </h2>

        <div style={{ textAlign: 'center' }}>
          <p style={{
            color: '#fff',
            fontSize: '1.2rem',
            fontWeight: '600',
            margin: '0 0 16px 0'
          }}>
            Hi {userName || "there"}! 👋
          </p>

          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '1rem',
            margin: '0 0 8px 0'
          }}>
            We've sent a verification link to:
          </p>

          <p style={{
            color: '#ff3366',
            fontSize: '1.1rem',
            fontWeight: '600',
            margin: '0 0 16px 0',
            wordBreak: 'break-all'
          }}>
            {userEmail || "your email"}
          </p>

          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.95rem',
            lineHeight: '1.6',
            margin: '0 0 24px 0'
          }}>
            Please check your inbox and click the link to verify your account.
            You must verify your email before accessing the dashboard.
          </p>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            margin: '28px 0 20px 0'
          }}>
            <button
              onClick={handleCheckVerification}
              disabled={checking}
              style={{
                width: '100%',
                padding: '14px',
                background: '#ff3366',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: checking ? 'not-allowed' : 'pointer',
                opacity: checking ? '0.5' : '1',
                transition: 'all 0.3s ease'
              }}
            >
              {checking ? "Checking..." : "I've Verified My Email"}
            </button>

            <button
              onClick={handleResendEmail}
              disabled={resending || isRateLimited}
              style={{
                width: '100%',
                padding: '13px',
                background: 'transparent',
                color: isRateLimited ? '#999' : '#ff3366',
                border: `2px solid ${isRateLimited ? '#999' : '#ff3366'}`,
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: (resending || isRateLimited) ? 'not-allowed' : 'pointer',
                opacity: (resending || isRateLimited) ? '0.4' : '1',
                transition: 'all 0.3s ease'
              }}
            >
              {resending ? "Sending..." : isRateLimited ? "Rate Limited (Wait 1 hour)" : "Resend Verification Email"}
            </button>
          </div>

          {message && (
            <p style={{
              color: message.includes("❌") || message.includes("⚠️") || message.includes("⏳") ? '#ff3366' : '#00ff88',
              background: message.includes("❌") || message.includes("⚠️") || message.includes("⏳") ? 'rgba(255, 51, 102, 0.1)' : 'rgba(0, 255, 136, 0.1)',
              border: message.includes("❌") || message.includes("⚠️") || message.includes("⏳") ? '1px solid rgba(255, 51, 102, 0.3)' : '1px solid rgba(0, 255, 136, 0.3)',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '0.95rem',
              margin: '10px 0'
            }}>
              {message}
            </p>
          )}

          <div style={{
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <p style={{ margin: '0' }}>
              <small style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.85rem',
                lineHeight: '1.5'
              }}>
                💡 Didn't receive the email? Check your spam folder. The verification email may take a few minutes to arrive.
              </small>
            </p>
          </div>

          {/* Contact Admin Box */}
          <ContactAdminBox />
        </div>
      </div>
    </div>
  );
}